import {
	MarkSpec,
	NodeSpec,
	Schema
} from "prosemirror-model";
import {
	marks as defaultMarks,
	nodes as defaultNodes
} from "prosemirror-schema-basic";
import { color } from "./marks/color";
import { heading } from "./nodes/heading";
import { paragraph } from "./nodes/paragraph";
import {
	bulletList as defaultBulletList,
	listItem as defaultListItem,
	orderedList as defaultOrderedList
} from "prosemirror-schema-list";
import {
	tableNodes
} from "prosemirror-tables";
import { divLike } from "./nodes/div";
import {
	entries,
	isNil,
	isNotNilOrEmpty
} from "client/Misc/utils";
import { image } from "./nodes/image";
import { table } from "./nodes/table";
import {
	getTableCellStyleAttributes,
	setTableCellStyleAttributes,
	TableCellStyleAttributes
} from "./nodes/tableCell";
import { table_row } from "./nodes/tableRow";
import { mention } from "./marks/mention";
import { fontSize } from "./marks/fontSize";
import { fontFamily } from "./marks/fontFamily";
import { underline } from "./marks/underline";
import { strikeThrough } from "./marks/strikeThrough";
import { link } from "./marks/link";

export type RichTextEditorNodes =
	| "doc"
	| "paragraph"
	| "blockquote"
	| "horizontal_rule"
	| "heading"
	| "code_block"
	| "text"
	| "image"
	| "hard_break"
	| "orderedList"
	| "bulletList"
	| "listItem"
	| "table"
	| "table_row"
	| "table_cell"
	| "table_header"
	| "divLike";

export type RichTextEditorMarks =
	| "link"
	| "em"
	| "underline"
	| "strikeThrough"
	| "strong"
	| "code"
	| "fontSize"
	| "fontFamily"
	| "color"
	| "mention";

export type RichTextEditorSchema = Schema<RichTextEditorNodes, RichTextEditorMarks>;

export function createSchema<TNodes extends RichTextEditorNodes, TMarks extends RichTextEditorMarks>(
	nodes: readonly TNodes[],
	marks: readonly TMarks[]
): Schema<TNodes, TMarks> {
	const nodeSet = new Set<TNodes>(nodes);
	const markSet = new Set<TMarks>(marks);

	const nodeMap: Record<TNodes, NodeSpec> = {} as Record<TNodes, NodeSpec>;
	const markMap: Record<TMarks, MarkSpec> = {} as Record<TMarks, MarkSpec>;

	for (const nodeEntry of entries(nodeToSpecMap)) {
		const {
			key,
			value
		} = nodeEntry;

		if (nodeSet.has(key as TNodes)) {
			nodeMap[key as TNodes] = value;
		}
	}

	for (const mapEntry of entries(markToSpecMap)) {
		const {
			key,
			value
		} = mapEntry;

		if (markSet.has(key as TMarks)) {
			markMap[key as TMarks] = value;
		}
	}

	return new Schema<TNodes, TMarks>({
		nodes: nodeMap,
		marks: markMap
	});
}

const {
	doc,
	blockquote,
	horizontal_rule,
	code_block,
	text,
	hard_break
} = defaultNodes;

const {
	em,
	strong,
	code
} = defaultMarks;

const {
	table_cell,
	table_header
} = tableNodes({
	tableGroup: "block",
	cellContent: "block+",
	cellAttributes: {
		style: {
			default: {},
			getFromDOM(node: Element): { [key: string]: string; } {
				if (node instanceof HTMLTableCellElement) {
					return getTableCellStyleAttributes(node);
				}
				return {};
			},
			setDOMAttr(
				value: unknown,
				attrs: { [key in string]: any; }
			): void {
				if (typeof(value) !== "object" || value === null) {
					throw new Error(`Expected a record object at this point.`);
				}
				attrs["style"] = setTableCellStyleAttributes(value as TableCellStyleAttributes);
			}
		},
		colwidth: {
			default: null,
			setDOMAttr(
				value: unknown,
				attrs: { [key in string]: any; }
			): void {
				if (isNil(value)) {
					return;
				}

				if (!Array.isArray(value) || value.some(x => typeof x !== "number")) {
					throw new Error(`Expected array of numbers at this point.`);
				}

				if (typeof attrs["style"] === "string") {
					attrs["style"] = attrs["style"].split(";")
						.filter(isNotNilOrEmpty)
						.filter(x => !x.startsWith("width:"))
						.join(";");
					if (attrs["style"] !== "") {
						attrs["style"] += ";";
					}
				} else {
					attrs["style"] = "";
				}

				const combinedWidth: number = value.reduce(
					(acc, cur) => acc + cur,
					0
				);

				attrs["style"] += `width: ${combinedWidth}px`;

			}
		}
	}
});

const orderedList: NodeSpec = {
	...defaultOrderedList,
	content: "listItem+",
	group: "block"
};

const bulletList: NodeSpec = {
	...defaultBulletList,
	content: "listItem+",
	group: "block"
};

const listItem: NodeSpec = {
	...defaultListItem,
	content: "paragraph block*"
};

export function hasNodes<
	TNodes extends RichTextEditorNodes,
	TOrigNodes extends RichTextEditorNodes,
	TOrigMarks extends RichTextEditorMarks
>(
	schema: Schema<TOrigNodes, TOrigMarks>,
	nodes: readonly TNodes[]
): schema is Schema<TNodes | TOrigNodes, TOrigMarks> {
	for (const node of nodes) {
		if (!schema.nodes[node]) {
			return false;
		}
	}

	return true;
}

export function hasMarks<
	TMarks extends RichTextEditorMarks,
	TOrigNodes extends RichTextEditorNodes,
	TOrigMarks extends RichTextEditorMarks
>(
	schema: Schema<TOrigNodes, TOrigMarks>,
	marks: readonly TMarks[]
): schema is Schema<TOrigNodes, TMarks | TOrigMarks> {
	for (const mark of marks) {
		if (!schema.marks[mark]) {
			return false;
		}
	}

	return true;
}

const nodeToSpecMap: {[key in RichTextEditorNodes]: NodeSpec} = {
	doc,
	paragraph,
	blockquote,
	horizontal_rule,
	heading,
	code_block,
	text,
	image,
	hard_break,
	orderedList,
	bulletList,
	listItem,
	table,
	table_row,
	table_cell,
	table_header,
	divLike
};

const markToSpecMap: {[key in RichTextEditorMarks]: MarkSpec} = {
	link,
	em,
	underline,
	strikeThrough,
	strong,
	code,
	fontSize,
	fontFamily,
	color,
	mention
};